import { type JSX, type ReactNode, useId, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  EREIGNISNAME,
  FOLGEAKTIONSNAME,
  KATEGORIENAME,
  STATUSNAME,
  notizAnlegen,
  type Ereigniszeile,
  type Notizzeile,
} from '../api/support';
import { alsZeitpunkt } from '../datum';
import { Meldung } from '../komponenten/Meldung';
import { fange } from './bearbeitungshilfe';

/**
 * DAS INTERNE EINES VORGANGS (SUP-21, SUP-23, SUP-24; Auftrag CRM-178).
 *
 * SUP-21: Notizen stehen in einer eigenen, anders gefärbten Fläche mit der
 * Kennzeichnung „Intern – geht nie an den Kunden" und neben (oder unter) dem
 * Kundenverkehr, nie dazwischen. Eine Notiz hat keinen Knopf zum Zitieren
 * oder Übernehmen — sie soll nicht versehentlich in eine Antwort geraten.
 */
export function Internbereich({
  vorgang,
  notizen,
  ereignisse,
  bearbeitbar,
  folgeaktionen,
  beiNotiz,
}: {
  readonly vorgang: string;
  readonly notizen: readonly Notizzeile[];
  readonly ereignisse: readonly Ereigniszeile[];
  readonly bearbeitbar: boolean;
  /** Die Liste der Folgeaktionen (SUP-23). */
  readonly folgeaktionen: ReactNode;
  readonly beiNotiz: () => Promise<void>;
}): JSX.Element {
  return (
    <section className="vorgang__intern" aria-label="Intern">
      {/* SUP-21 */}
      <p className="vorgang__intern-kennung">Intern – geht nie an den Kunden</p>

      <h3>Notizen</h3>
      {notizen.length === 0 ? (
        <p className="hilfe">Noch keine Notiz.</p>
      ) : (
        <ul className="vorgang__notizen" aria-label="Interne Notizen">
          {notizen.map((n) => (
            <li key={n.id}>
              <p className="vorgang__notiztext">{n.text}</p>
              <span className="hilfe">
                {n.von} · {alsZeitpunkt(n.am) ?? n.am}
              </span>
            </li>
          ))}
        </ul>
      )}
      {bearbeitbar && <Notizeingabe vorgang={vorgang} beiGespeichert={beiNotiz} />}

      <h3>Folgeaktionen</h3>
      {folgeaktionen}

      {/* SUP-17, SUP-18, SUP-24: wer wann was am Vorgang tat. */}
      {ereignisse.length > 0 && (
        <details>
          <summary>Verlauf der Bearbeitung</summary>
          <ul className="vorgang__notizen" aria-label="Verlauf der Bearbeitung">
            {ereignisse.map((e, i) => (
              <li key={`${e.am}-${String(i)}`}>
                {EREIGNISNAME[e.art]}
                {ereigniszusatz(e)}
                <br />
                <span className="hilfe">
                  {e.wer} · {alsZeitpunkt(e.am) ?? e.am}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

/** Ein lesbarer Zusatz aus den Daten eines Ereignisses — Kennungen bleiben weg. */
function ereigniszusatz(e: Ereigniszeile): string {
  const d = e.daten;
  const nachher = d['nachher'];
  const text = (wert: unknown): string | null => {
    if (typeof wert !== 'string') return null;
    if (e.art === 'status' && wert in STATUSNAME) return STATUSNAME[wert as keyof typeof STATUSNAME];
    if (e.art === 'kategorie' && wert in KATEGORIENAME) return KATEGORIENAME[wert as keyof typeof KATEGORIENAME];
    if (e.art === 'faelligkeit') return alsZeitpunkt(wert);
    return null;
  };
  if (e.art === 'status' || e.art === 'kategorie' || e.art === 'faelligkeit') {
    const n = text(nachher);
    if (n !== null) return `: ${n}`;
    if (nachher === null) return ': entfernt';
    return '';
  }
  if (e.art === 'folgeaktion' && typeof d['art'] === 'string' && d['art'] in FOLGEAKTIONSNAME) {
    return `: ${FOLGEAKTIONSNAME[d['art'] as keyof typeof FOLGEAKTIONSNAME]}`;
  }
  if (typeof d['nummer'] === 'string') return `: ${d['nummer']}`;
  if (typeof d['name'] === 'string') return `: ${d['name']}`;
  return '';
}

/** SUP-21: eine interne Notiz schreiben. */
function Notizeingabe({
  vorgang,
  beiGespeichert,
}: {
  readonly vorgang: string;
  readonly beiGespeichert: () => Promise<void>;
}): JSX.Element {
  const kennung = useId();
  const [text, setzeText] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);

  const speichern = async (): Promise<void> => {
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      await notizAnlegen(vorgang, text.trim());
      setzeText('');
      await beiGespeichert();
    } catch (f) {
      fange(setzeFehler, f);
    } finally {
      setzeLaeuft(false);
    }
  };

  return (
    <div className="feld">
      {fehler !== null && <Meldung fehler={fehler} />}
      <label className="feld__beschriftung" htmlFor={`${kennung}-notiz`}>Interne Notiz</label>
      <textarea
        id={`${kennung}-notiz`}
        className="feld__eingabe"
        rows={3}
        value={text}
        onChange={(e) => setzeText(e.target.value)}
      />
      <div className="knopfreihe">
        <button
          type="button"
          className="knopf knopf--haupt knopf--klein"
          disabled={laeuft || text.trim() === ''}
          onClick={() => void speichern()}
        >
          Notiz speichern
        </button>
      </div>
    </div>
  );
}
